/**
 * Protocol v1 events → session state. Pure and synchronous: the live stream and a page reload go through
 * the same code (reload replays the session's stored events), so what the creator sees can't drift from
 * what the runtime said. Tests replay real event sequences recorded from the backend acceptance runs.
 */
import type {
    AttachmentPayload,
    PayloadMap,
    AgentEventType,
    AnyAgentEvent,
    BrowserController,
    BrowserTaskCard,
    ClientCallAction,
    ClientCallStatus,
    EmailCard,
    LinkCard,
    SuggestionChip,
    ComposerEditTarget,
    JobPayload,
    RunStatus,
    TodoItem,
    VoiceSayKind,
} from './protocol/types'

/** A file the creator attached to their message. */
export interface UiAttachment {
    /** Its ``files`` row, once uploaded. */
    fileId?: string
    name: string
    mime?: string | null
    size?: number | null
    /** The creator's own copy (this tab sent it): previews without a download. */
    file?: File
}

export interface UiMessage {
    id: string
    role: 'user' | 'assistant'
    text: string
    streaming: boolean
    runId: string | null
    pending?: boolean
    ts?: string
    /** One-tap replies offered under this message (sent as the creator's message when tapped). */
    suggestions?: SuggestionChip[]
    /** A browser task proposed with this message: the card with the Start browser button. */
    browserTask?: BrowserTaskCard
    attachments?: UiAttachment[]
    /** The email an agent-started message is about (inbox conversations): the card above the note. */
    email?: EmailCard
    /** Links the creator has to open themselves (an agreement to sign): cards under the note. */
    links?: LinkCard[]
    /** The creator's messages: spoken on a voice call (a mic glyph on the bubble). */
    inputMode?: 'text' | 'voice'
    /** Said on a voice call by the call's voice (the talker), not written by the agent. */
    spoken?: boolean
    /** The optimistic copy's id this server message replaced: the UI keeps that row (no remount, no second entrance). */
    localKey?: string
}

/** A voice call in the conversation, as its `call.updated` events left it. */
/** A line the call's voice said (or will say) while the agent worked (`voice.say`). */
export interface UiVoiceSay {
    id: string
    runId: string | null
    kind: VoiceSayKind
    text: string
    /** The chat message it's also recorded as (failures, answers about the work). */
    messageId: string | null
    /** What it answers: the run (its start) or the id of words said mid-task (a steer). */
    replyTo: string | null
    ts: string
}

export interface UiCall {
    id: string
    status: 'started' | 'ended'
    startedAt: string
    endedAt: string | null
    durationMs: number | null
    turns: number | null
    reason: string | null
}

/** The conversation's live browser, as the last `browser.state` event left it. */
export interface UiBrowser {
    status: 'starting' | 'active' | 'closed' | 'failed'
    controller: BrowserController
    taskId: string | null
    taskStatus: string | null
    handoffReason: string | null
    requestedBy: BrowserController | null
    url: string | null
    title: string | null
    tabs: number | null
}

export interface UiRun {
    id: string
    status: RunStatus
    tier?: string
    model?: string
    rounds?: number
    error?: string | null
    resumed?: boolean
    /** Event timestamps, for "Worked for 12s" on a finished turn. */
    startedAt?: string
    finishedAt?: string
}

export interface UiToolCall {
    callId: string
    runId: string | null
    tool: string
    label: string
    code?: string
    /** When the call started (the event's time), for the live "· 42s" on a long step. */
    startedAt?: string
    /** What the agent told the creator right before this step ("Same numbers as the plan. Applying now."). */
    note?: string
    status: 'running' | 'ok' | 'error'
    durationMs?: number
    summary?: string
    error?: string | null
    changes: string[]
}

export type ChangesetState = 'draft' | 'applied' | 'pending_approval' | 'rejected' | 'undone'

export interface UiChangeset {
    id: string
    runId: string | null
    state: ChangesetState
    summary: string[]
    maxRisk: string | null
    reasons: string[]
    notUndone: string[]
    /** Seqs of ops whose records were put back one at a time (from the changes pane), once known here. */
    undone?: number[]
}

/** The form has its own "Something else" line, so drop catch-all choices the agent wrote anyway. */
const CATCH_ALL_OPTION = /^\W*(something|anything)\s+else\b|^\W*none\s+of\s+the\s+above\W*$|^\W*others?\b(\s*\(.*\)|\s*[-:–—].*|\s+option)?\W*$/i
const withoutCatchAll = (options?: string[]): string[] => (options ?? []).filter((o) => !CATCH_ALL_OPTION.test(o.trim()))

/** One of several questions asked together (`ask_user.questions`). */
export interface UiAskField {
    question: string
    options: string[]
    multi: boolean
    optional: boolean
}

export interface UiQuestion {
    id: string
    question: string
    options: string[]
    fields: UiAskField[]
    runId: string | null
    answered: boolean
}

export interface UiJob extends JobPayload {
    error?: string | null
}

export interface UiReviewItem {
    id: string
    jobId: string | null
    title: string
    summary: string[]
    similarityKey: string | null
    changesetId: string | null
    status: 'pending' | 'approved' | 'rejected' | 'skipped' | 'failed'
    error?: string | null
}

export interface UiAction {
    eventId: number
    action: 'navigate' | 'open' | 'highlight' | 'filter' | 'insert_into_composer'
    target: Record<string, any>
    runId: string | null
}

export interface UiComposerEdit {
    key: string
    eventId: number
    target: ComposerEditTarget
    state: 'proposed' | 'accepted' | 'rejected'
}

/** The agent's ask to use the creator's computer (`client.call`): the desktop card follows it. */
export interface UiClientCall {
    id: string
    runId: string | null
    action: ClientCallAction
    why: string
    /** open_url: the link. */
    url: string | null
    status: ClientCallStatus
    expiresAt: string
    /** The desktop window that may answer it (the one that sent the turn's message). */
    clientId: string
}

export type TimelineItem =
    | { kind: 'message'; id: string }
    | { kind: 'tools'; runId: string }
    | { kind: 'changeset'; id: string }
    | { kind: 'question'; id: string }
    | { kind: 'job'; id: string }
    | { kind: 'composer_edit'; key: string }
    | { kind: 'error'; id: string }
    /** The agent opened or pointed at one record (`ui.action` open / navigate / highlight with an id). */
    | { kind: 'action'; eventId: number }
    /** The agent asked to use the creator's computer (desktop app). */
    | { kind: 'client_call'; id: string }
    /** A voice call's start and end lines ("Voice call · 10:01 AM", "Voice call ended · 4:12"). */
    | { kind: 'call'; id: string; phase: 'start' | 'end' }

export interface SessionState {
    sessionId: string | null
    title: string | null
    /** Who started the conversation: 'user', or 'agent:…' for check-ins the agent opened. */
    initiatedBy: string | null
    /** The email thread the conversation is about, from the session detail: always offered in the workspace. */
    linkedThread: string | null
    /** Every thread it's about, in the order they joined (a deal's second thread joins its first one's conversation). */
    linkedThreads: string[]
    /** The brand the conversation is about, from the session detail: its logo sits beside the title. */
    linkedBrand: { id: string; name: string; domain: string | null } | null
    cursor: number
    timeline: TimelineItem[]
    messages: Record<string, UiMessage>
    runs: Record<string, UiRun>
    activeRunId: string | null
    todo: TodoItem[]
    todoRunId: string | null
    tools: Record<string, UiToolCall>
    toolOrder: Record<string, string[]>
    changesets: Record<string, UiChangeset>
    question: UiQuestion | null
    questions: Record<string, UiQuestion>
    jobs: Record<string, UiJob>
    review: Record<string, UiReviewItem>
    progress: Record<string, { label: string; done: number; total: number }>
    errors: Record<string, { id: string; message: string; code?: string }>
    actions: UiAction[]
    composerEdits: Record<string, UiComposerEdit>
    /** Browser task cards by task id; status follows `browser.state` (and the session detail on reload). */
    browserTasks: Record<string, BrowserTaskCard>
    browser: UiBrowser
    /** Desktop asks by call id (`client.call`), status from `client.call_resolved`. */
    clientCalls: Record<string, UiClientCall>
    /** Voice calls by call id. */
    calls: Record<string, UiCall>
    /** What the call's voice said while the agent worked, oldest first (the last 50). */
    voiceSays: UiVoiceSay[]
    handledTypes: Partial<Record<AgentEventType, number>>
    /** The creator acted on something here (approved or turned down a change, undid one, decided a review item, answered a desktop ask). */
    creatorActed?: boolean
}

export function initialBrowser(): UiBrowser {
    return { status: 'closed', controller: 'agent', taskId: null, taskStatus: null, handoffReason: null, requestedBy: null, url: null, title: null, tabs: null }
}

export function initialState(sessionId: string | null = null): SessionState {
    return {
        sessionId, title: null, initiatedBy: null, linkedThread: null, linkedThreads: [], linkedBrand: null, cursor: 0, timeline: [], messages: {}, runs: {}, activeRunId: null, todo: [],
        todoRunId: null, tools: {}, toolOrder: {}, changesets: {}, question: null, questions: {}, jobs: {},
        review: {}, progress: {}, errors: {}, actions: [], composerEdits: {}, browserTasks: {}, browser: initialBrowser(),
        clientCalls: {}, calls: {}, voiceSays: [], handledTypes: {},
    }
}

function push(state: SessionState, item: TimelineItem): void {
    const key = JSON.stringify(item)
    if (!state.timeline.some((t) => JSON.stringify(t) === key)) state.timeline.push(item)
}

/** Take a streamed message that turned out to be a step's note out of the transcript. */
function foldIntoSteps(state: SessionState, m: UiMessage): void {
    delete state.messages[m.id]
    state.timeline = state.timeline.filter((t) => !(t.kind === 'message' && t.id === m.id))
}

function changesetState(type: AgentEventType, status: string): ChangesetState {
    if (type === 'changeset.undone') return 'undone'
    if (type === 'changeset.pending_approval' || status === 'pending_approval') return 'pending_approval'
    if (type === 'changeset.applied' || status === 'applied') return 'applied'
    if (status === 'rejected') return 'rejected'
    return 'draft'
}

/** Apply one event. Mutates and returns `state` (callers wrap it in a reactive object). */
export function reduce(state: SessionState, ev: AnyAgentEvent): SessionState {
    if (ev.id <= state.cursor) return state
    state.cursor = ev.id
    state.handledTypes[ev.type] = (state.handledTypes[ev.type] ?? 0) + 1
    const runId = ev.run_id
    switch (ev.type) {
        case 'session.created':
            state.title = ev.payload.title
            break
        case 'run.started': {
            state.runs[runId!] = { ...(state.runs[runId!] ?? { id: runId! }), id: runId!, status: 'running',
                                   tier: ev.payload.tier, model: ev.payload.model, resumed: ev.payload.resumed,
                                   startedAt: state.runs[runId!]?.startedAt ?? ev.ts }
            state.activeRunId = runId
            // A new run in the session means the open question was answered.
            if (state.question && state.question.runId !== runId) {
                state.questions[state.question.id] = { ...state.question, answered: true }
                state.question = null
            }
            break
        }
        case 'run.finished': {
            const r = state.runs[runId!] ?? { id: runId!, status: 'running' }
            state.runs[runId!] = { ...r, status: ev.payload.status, rounds: ev.payload.rounds,
                                   error: ev.payload.error ?? null, finishedAt: ev.ts }
            if (state.activeRunId === runId) state.activeRunId = null
            for (const m of Object.values(state.messages)) if (m.runId === runId) m.streaming = false
            if (ev.payload.status !== 'waiting_user' && state.question && state.question.runId === runId) {
                state.question = null
            }
            if (ev.payload.status === 'failed' && ev.payload.error) {
                const id = `run-${runId}`
                state.errors[id] = { id, message: ev.payload.error }
                push(state, { kind: 'error', id })
            }
            // A draft changeset that never got committed (cancelled/failed run) was discarded.
            if (ev.payload.status === 'cancelled' || ev.payload.status === 'failed') {
                for (const c of Object.values(state.changesets)) {
                    if (c.runId === runId && c.state === 'draft') c.state = 'rejected'
                }
            }
            // Nothing waits on a desktop ask once its run is over (normally it was closed before the run ended).
            for (const c of Object.values(state.clientCalls)) {
                if (c.runId === runId && c.status === 'pending') c.status = 'cancelled'
            }
            break
        }
        case 'message.delta': {
            const id = ev.payload.message_id
            const m = state.messages[id]
            if (ev.payload.interim) {
                // A step started after this text: it was a note about the step (it rides on tool.started), not the reply.
                if (m) foldIntoSteps(state, m)
                break
            }
            if (m) {
                m.text += ev.payload.text
            } else {
                state.messages[id] = { id, role: 'assistant', text: ev.payload.text, streaming: true, runId, ts: ev.ts }
                push(state, { kind: 'message', id })
            }
            break
        }
        case 'message.completed': {
            const { message_id: id, role, text, suggestions, browser_task, email, links } = ev.payload
            const inputMode = {
                ...(role === 'user' && ev.payload.input_mode ? { inputMode: ev.payload.input_mode } : {}),
                ...(ev.payload.spoken ? { spoken: true } : {}),
            }
            if (browser_task) state.browserTasks[browser_task.task_id] = { ...(state.browserTasks[browser_task.task_id] ?? {}), ...browser_task }
            if (role === 'user') {
                // Replace the optimistic local copy of what the creator just sent.
                const local = Object.values(state.messages).find((m) => m.pending && m.role === 'user' && m.text === text)
                const attachments = sentAttachments(ev.payload.attachments, local?.attachments)
                if (local) {
                    delete state.messages[local.id]
                    const at = state.timeline.findIndex((t) => t.kind === 'message' && t.id === local.id)
                    state.messages[id] = { id, role, text, streaming: false, runId, ts: ev.ts, localKey: local.localKey ?? local.id,
                                           ...(attachments.length ? { attachments } : {}), ...(local.inputMode ? { inputMode: local.inputMode } : {}), ...inputMode }
                    if (at >= 0) state.timeline[at] = { kind: 'message', id }
                    else push(state, { kind: 'message', id })
                    break
                }
            }
            const m = state.messages[id]
            const files = role === 'user' ? sentAttachments(ev.payload.attachments) : []
            state.messages[id] = { ...(m ?? { id, role, runId, ts: ev.ts }), id, role, text, streaming: false,
                                   ...(suggestions?.length ? { suggestions } : {}),
                                   ...(files.length ? { attachments: files } : {}),
                                   ...(browser_task ? { browserTask: browser_task } : {}),
                                   ...(email ? { email } : {}), ...(links?.length ? { links } : {}), ...inputMode } as UiMessage
            push(state, { kind: 'message', id })
            break
        }
        case 'todo.updated':
            state.todo = ev.payload.items
            state.todoRunId = runId
            break
        case 'tool.started': {
            const p = ev.payload
            state.tools[p.call_id] = { callId: p.call_id, runId, tool: p.tool, label: p.label, code: p.code,
                                       startedAt: ev.ts, status: 'running', changes: [], ...(p.note ? { note: p.note } : {}) }
            // Text still streaming in this run when a step starts was the note before the step, never the reply
            // (runtimes before `interim` left it hanging as a reply cut off mid-sentence, with the turn looking done).
            for (const m of Object.values(state.messages)) {
                if (m.runId === runId && m.role === 'assistant' && m.streaming) foldIntoSteps(state, m)
            }
            const key = runId ?? 'none'
            state.toolOrder[key] = [...(state.toolOrder[key] ?? []), p.call_id]
            push(state, { kind: 'tools', runId: key })
            break
        }
        case 'tool.finished': {
            const p = ev.payload
            const t = state.tools[p.call_id] ?? { callId: p.call_id, runId, tool: p.tool, label: p.tool, changes: [] }
            state.tools[p.call_id] = { ...t, status: p.ok ? 'ok' : 'error', durationMs: p.duration_ms,
                                       summary: p.summary, error: p.error ?? null, changes: p.changes?.new ?? [] } as UiToolCall
            break
        }
        case 'progress': {
            const key = ev.payload.job_id ?? runId ?? ev.payload.label
            state.progress[key] = { label: ev.payload.label, done: ev.payload.done, total: ev.payload.total }
            break
        }
        case 'changeset.proposed':
        case 'changeset.applied':
        case 'changeset.pending_approval':
        case 'changeset.undone': {
            const p = ev.payload as any
            const prev = state.changesets[p.changeset_id]
            const next = changesetState(ev.type, p.status)
            if (ev.type === 'changeset.undone' || (prev?.state === 'pending_approval' && next !== 'pending_approval')) state.creatorActed = true
            state.changesets[p.changeset_id] = {
                id: p.changeset_id,
                runId: prev?.runId ?? runId,
                state: next,
                summary: p.summary?.length ? p.summary : prev?.summary ?? [],
                maxRisk: p.max_risk ?? prev?.maxRisk ?? null,
                reasons: p.reasons ?? prev?.reasons ?? [],
                notUndone: p.not_undone ?? prev?.notUndone ?? [],
            }
            // One card per turn: a later card for the same run replaces the draft preview.
            if (ev.type !== 'changeset.undone') {
                for (const [id, c] of Object.entries(state.changesets)) {
                    if (id !== p.changeset_id && c.runId && c.runId === runId && c.state === 'draft') {
                        delete state.changesets[id]
                        state.timeline = state.timeline.filter((t) => !(t.kind === 'changeset' && t.id === id))
                    }
                }
            }
            push(state, { kind: 'changeset', id: p.changeset_id })
            break
        }
        case 'job.created':
        case 'job.updated': {
            const p = ev.payload as any
            state.jobs[p.job_id] = { ...(state.jobs[p.job_id] ?? {}), ...p, error: p.error ?? null }
            if (ev.type === 'job.created') push(state, { kind: 'job', id: p.job_id })
            break
        }
        case 'review.item_added': {
            const p = ev.payload
            state.review[p.review_id] = { id: p.review_id, jobId: p.job_id, title: p.title, summary: p.summary,
                                          similarityKey: p.similarity_key, changesetId: p.changeset_id, status: 'pending' }
            break
        }
        case 'review.item_decided': {
            const p = ev.payload
            const r = state.review[p.review_id]
            if (r) state.review[p.review_id] = { ...r, status: p.status, error: p.error ?? null }
            if (p.status === 'approved' || p.status === 'rejected' || p.status === 'skipped') state.creatorActed = true
            if (p.changeset_id && p.status === 'approved' && state.changesets[p.changeset_id]) {
                state.changesets[p.changeset_id].state = 'applied'
            }
            break
        }
        case 'ask_user': {
            const p = ev.payload
            const fields = (p.questions ?? []).map((f) => ({ question: f.question, options: withoutCatchAll(f.options), multi: Boolean(f.multi), optional: Boolean(f.optional) }))
            const q: UiQuestion = { id: p.question_id, question: p.question, options: withoutCatchAll(p.options), fields, runId, answered: false }
            state.questions[q.id] = q
            state.question = q
            push(state, { kind: 'question', id: q.id })
            break
        }
        case 'ui.action': {
            const p = ev.payload
            if (p.action === 'insert_into_composer') {
                const key = String(p.target?.call_id ?? ev.id)
                state.composerEdits[key] = { key, eventId: ev.id, target: p.target as ComposerEditTarget, state: 'proposed' }
                push(state, { kind: 'composer_edit', key })
            }
            state.actions.push({ eventId: ev.id, action: p.action, target: p.target, runId })
            if (p.action !== 'insert_into_composer' && p.action !== 'filter' && p.target?.type && p.target?.id && !Array.isArray(p.target?.ids)) {
                push(state, { kind: 'action', eventId: ev.id })
            }
            break
        }
        case 'browser.state': {
            const p = ev.payload
            state.browser = {
                status: p.status, controller: p.controller, taskId: p.task_id ?? state.browser.taskId,
                taskStatus: p.task_status ?? (p.task_id ? state.browser.taskStatus : null),
                handoffReason: p.controller === 'user' ? p.handoff_reason ?? null : null,
                requestedBy: p.controller === 'user' ? p.requested_by ?? null : null,
                url: p.url ?? (p.status === 'active' ? state.browser.url : null), title: p.title ?? (p.status === 'active' ? state.browser.title : null),
                tabs: p.tabs ?? (p.status === 'active' ? state.browser.tabs : null),
            }
            if (p.task_id && p.task_status) {
                const t = state.browserTasks[p.task_id]
                if (t) t.status = p.task_status
            }
            break
        }
        case 'client.call': {
            const p = ev.payload
            const prev = state.clientCalls[p.call_id]
            state.clientCalls[p.call_id] = {
                id: p.call_id, runId, action: p.action, why: p.why, url: p.args?.url ?? null,
                status: prev?.status ?? 'pending', expiresAt: p.expires_at, clientId: p.client_id,
            }
            push(state, { kind: 'client_call', id: p.call_id })
            break
        }
        case 'client.call_resolved': {
            const c = state.clientCalls[ev.payload.call_id]
            if (c) c.status = ev.payload.status
            if (ev.payload.status === 'completed' || ev.payload.status === 'denied') state.creatorActed = true
            break
        }
        case 'call.updated': {
            const p = ev.payload
            const prev = state.calls[p.call_id]
            state.calls[p.call_id] = {
                id: p.call_id,
                // An end never goes back to started (events can repeat on reconnect).
                status: prev?.status === 'ended' ? 'ended' : p.status,
                startedAt: p.started_at || prev?.startedAt || ev.ts,
                endedAt: p.ended_at ?? prev?.endedAt ?? (p.status === 'ended' ? ev.ts : null),
                durationMs: p.duration_ms ?? prev?.durationMs ?? null,
                turns: p.turns ?? prev?.turns ?? null,
                reason: p.reason ?? prev?.reason ?? null,
            }
            if (p.status === 'started') push(state, { kind: 'call', id: p.call_id, phase: 'start' })
            else push(state, { kind: 'call', id: p.call_id, phase: 'end' })
            break
        }
        case 'voice.say': {
            const p = ev.payload
            if (state.voiceSays.some((v) => v.id === p.say_id)) break
            state.voiceSays.push({ id: p.say_id, runId: runId ?? null, kind: p.kind, text: p.text, messageId: p.message_id ?? null, replyTo: p.reply_to ?? null, ts: ev.ts })
            if (state.voiceSays.length > 50) state.voiceSays.splice(0, state.voiceSays.length - 50)
            break
        }
        case 'error': {
            const id = `ev-${ev.id}`
            state.errors[id] = { id, message: ev.payload.message, code: ev.payload.code }
            push(state, { kind: 'error', id })
            break
        }
    }
    return state
}

export function reduceAll(state: SessionState, events: AnyAgentEvent[]): SessionState {
    for (const e of events) reduce(state, e)
    return state
}

/** The server's list of a message's files, keeping this tab's own copies (matched by file id) for previews. */
function sentAttachments(sent: AttachmentPayload[] | undefined, local?: UiAttachment[]): UiAttachment[] {
    if (!sent?.length) return local?.filter((a) => a.fileId) ?? []
    return sent.map((a) => {
        const mine = local?.find((l) => l.fileId === a.file_id)
        return { fileId: a.file_id, name: a.filename || mine?.name || 'Attachment', mime: a.content_type ?? mine?.mime ?? null,
                 size: a.size ?? mine?.size ?? null, ...(mine?.file ? { file: mine.file } : {}) }
    })
}

/** Optimistic copy of the creator's message until the server echoes it. */
export function addLocalUserMessage(state: SessionState, text: string, files: File[] = [], inputMode?: 'text' | 'voice'): string {
    const id = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const attachments = files.map((f) => ({ name: f.name, mime: f.type || null, size: f.size, file: f }))
    state.messages[id] = { id, role: 'user', text, streaming: false, runId: null, pending: true,
                           ...(attachments.length ? { attachments } : {}), ...(inputMode === 'voice' ? { inputMode } : {}) }
    state.timeline.push({ kind: 'message', id })
    return id
}

/** A transcript row as the session detail returns it (api.ts `TranscriptMessage`). */
export interface SeedMessage {
    id: string
    role: 'user' | 'assistant'
    text: string
    created_at: string
    suggestions?: SuggestionChip[]
    browser_task?: BrowserTaskCard
    attachments?: AttachmentPayload[]
    /** The creator's messages: the run it started (the live event's message id). */
    run_id?: string
    input_mode?: 'text' | 'voice'
    email?: EmailCard
    links?: LinkCard[]
    spoken?: boolean
}

/** Seed messages the event log no longer has (events are pruned after 30 days). */
export function seedTranscript(state: SessionState, messages: SeedMessage[], beforeTs?: string): void {
    for (const m of messages) {
        if (beforeTs && m.created_at >= beforeTs) continue
        if (state.messages[m.id]) continue
        const files = sentAttachments(m.attachments)
        state.messages[m.id] = { id: m.id, role: m.role, text: m.text, streaming: false, runId: null, ts: m.created_at,
                                 ...(m.role === 'user' && m.input_mode ? { inputMode: m.input_mode } : {}),
                                 ...(m.spoken ? { spoken: true } : {}),
                                 ...(m.suggestions?.length ? { suggestions: m.suggestions } : {}),
                                 ...(m.browser_task ? { browserTask: m.browser_task } : {}),
                                 ...(m.email ? { email: m.email } : {}), ...(m.links?.length ? { links: m.links } : {}),
                                 ...(files.length ? { attachments: files } : {}) }
        if (m.browser_task) state.browserTasks[m.browser_task.task_id] = { ...(state.browserTasks[m.browser_task.task_id] ?? {}), ...m.browser_task }
        state.timeline.push({ kind: 'message', id: m.id })
    }
}

/** Files on the creator's messages from before events carried them: the transcript has them by run id. */
export function seedAttachments(state: SessionState, messages: SeedMessage[] | undefined): void {
    for (const m of messages ?? []) {
        const target = m.run_id ? state.messages[m.run_id] : undefined
        if (!target || target.role !== 'user' || target.attachments?.length) continue
        const files = sentAttachments(m.attachments)
        if (files.length) target.attachments = files
    }
}

/** The session detail's current task statuses and browser state (cards in old events may be stale). */
export function seedBrowser(state: SessionState, tasks: { id: string; status: string; title: string; domain: string; suspicious?: boolean }[] | undefined,
                            browser: PayloadMap['browser.state'] | undefined): void {
    for (const t of tasks ?? []) {
        const prev = state.browserTasks[t.id]
        state.browserTasks[t.id] = { ...(prev ?? { task_id: t.id, title: t.title, domain: t.domain, suspicious: t.suspicious }), status: t.status }
    }
    if (browser) {
        state.browser = { ...initialBrowser(), status: browser.status, controller: browser.controller, taskId: browser.task_id ?? null,
                          taskStatus: browser.task_status ?? null, handoffReason: browser.handoff_reason ?? null,
                          requestedBy: browser.requested_by ?? null, url: browser.url ?? null, title: browser.title ?? null, tabs: browser.tabs ?? null }
    }
}

/** The card's current status: the live task state when known, else what the message carried. */
export function browserTaskFor(s: SessionState, m: UiMessage): BrowserTaskCard | null {
    if (!m.browserTask) return null
    return s.browserTasks[m.browserTask.task_id] ?? m.browserTask
}

// ── selectors ────────────────────────────────────────────────────────────
/** Chips to show: only under the newest message, only when it's the agent's and nothing is in flight. An open
 *  question with its own options or fields has its form instead; a plain open question can take chips. Any other
 *  card waiting on the creator (changes to approve, a suggested edit to accept) hides them until it's resolved. */
export function activeSuggestions(s: SessionState): SuggestionChip[] {
    const last = [...s.timeline].reverse().find((t) => t.kind === 'message')
    const m = last && last.kind === 'message' ? s.messages[last.id] : undefined
    const q = s.question && !s.question.answered ? s.question : null
    if (!m || m.role !== 'assistant' || isWorking(s) || (q && (q.options?.length || q.fields?.length))) return []
    if (pendingApprovals(s).length || Object.values(s.composerEdits).some((e) => e.state === 'proposed')) return []
    return m.suggestions ?? []
}

/**
 * The creator has taken part: they replied and got the agent's answer back, or acted on something in the
 * conversation (a change approved, turned down or undone, a review item decided, a desktop ask answered, a
 * composer edit taken or dropped). Agent-started conversations offer "Done" from then on.
 */
export function creatorEngaged(s: SessionState): boolean {
    if (s.creatorActed || Object.values(s.composerEdits).some((e) => e.state !== 'proposed')) return true
    const isUser = (t: TimelineItem) => t.kind === 'message' && s.messages[t.id]?.role === 'user'
    let lastUser = -1
    s.timeline.forEach((t, i) => { if (isUser(t)) lastUser = i })
    if (lastUser < 0) return false
    return s.timeline.slice(lastUser + 1).some((t) => t.kind !== 'message' || (s.messages[t.id]?.role === 'assistant' && !s.messages[t.id].streaming))
}

export function isWorking(s: SessionState): boolean {
    return Boolean(s.activeRunId && s.runs[s.activeRunId]?.status === 'running')
}

export function toolsForRun(s: SessionState, runId: string): UiToolCall[] {
    return (s.toolOrder[runId] ?? []).map((id) => s.tools[id]).filter(Boolean)
}

/** A desktop ask's status as of `now`: a pending one past its `expires_at` reads as expired (nobody can answer it). */
export function clientCallStatus(c: Pick<UiClientCall, 'status' | 'expiresAt'>, now = Date.now()): ClientCallStatus {
    if (c.status !== 'pending') return c.status
    const at = Date.parse(c.expiresAt)
    return Number.isFinite(at) && at <= now ? 'expired' : 'pending'
}

/** After a replay: asks still pending in the log but past their time are expired (the run stopped waiting). */
export function expireClientCalls(s: SessionState, now = Date.now()): void {
    for (const c of Object.values(s.clientCalls)) if (clientCallStatus(c, now) === 'expired') c.status = 'expired'
}

/** The newest ask this window (`clientId`) can still answer: the one Enter / Escape act on. */
export function activeClientCall(s: SessionState, clientId: string | null, now = Date.now()): UiClientCall | null {
    if (!clientId) return null
    for (let i = s.timeline.length - 1; i >= 0; i--) {
        const t = s.timeline[i]
        if (t.kind !== 'client_call') continue
        const c = s.clientCalls[t.id]
        if (c && c.clientId === clientId && clientCallStatus(c, now) === 'pending') return c
    }
    return null
}

export function pendingApprovals(s: SessionState): UiChangeset[] {
    return Object.values(s.changesets).filter((c) => c.state === 'pending_approval')
}

export function lastAssistantText(s: SessionState): string {
    for (let i = s.timeline.length - 1; i >= 0; i--) {
        const t = s.timeline[i]
        if (t.kind === 'message' && s.messages[t.id]?.role === 'assistant') return s.messages[t.id].text
    }
    return ''
}
